'use client';
// https://github.com/mui/material-ui/blob/5ae79e6c305e3b2ba68e178c7d2774257e6783bd/examples/material-ui-nextjs-pages-router-ts/src/Link.tsx

import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';
import { forwardRef } from 'react';
import type { LinkProps as NextLinkProps } from 'next/link';
import NextLink from 'next/link';
import type { LinkProps as MuiLinkProps } from '@mui/material/Link';
import MuiLink from '@mui/material/Link';
import { styled } from '@mui/material/styles';

// Add support for the sx prop for consistency with the other branches.
const Anchor = styled('a')({});

interface NextLinkComposedProps
  extends
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>,
    Omit<
      NextLinkProps,
      'href' | 'as' | 'passHref' | 'onMouseEnter' | 'onClick' | 'onTouchStart'
    > {
  href: NextLinkProps['href'];
}

export const NextLinkComposed = forwardRef<
  HTMLAnchorElement,
  NextLinkComposedProps
>(function NextLinkComposed(props, ref) {
  const { href, replace, scroll, shallow, prefetch, locale, ...other } = props;

  return (
    <NextLink
      href={href}
      prefetch={prefetch}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      passHref
      locale={locale}
      ref={ref}
    >
      <Anchor {...other} />
    </NextLink>
  );
});

export type LinkProps = {
  activeClassName?: string;
  as?: NextLinkProps['as'];
  href: NextLinkProps['href'];
  noMuiStyle?: boolean;
  isExternal?: boolean;
} & Omit<NextLinkComposedProps, 'href'> &
  Omit<MuiLinkProps, 'href'>;

// A styled version of the Next.js Link component:
// https://nextjs.org/docs/pages/api-reference/components/link
export const Or2Link = forwardRef<HTMLAnchorElement, LinkProps>(
  function Or2Link(props, ref) {
    const {
      activeClassName = 'active',
      as,
      className: classNameProps,
      href,
      locale,
      noMuiStyle,
      prefetch,
      replace,
      role, // Link don't have roles.
      scroll,
      shallow,
      isExternal,
      underline = 'hover',
      ...other
    } = props;

    const routerPathname = usePathname();
    const pathname = typeof href === 'string' ? href : href.pathname;

    const className = clsx(classNameProps, {
      [activeClassName]: routerPathname === pathname && activeClassName,
    });

    const nextjsProps = {
      href,
      replace,
      scroll,
      shallow,
      prefetch,
      locale,
      target: isExternal ? '_blank' : undefined,
      rel: isExternal ? 'noopener noreferrer' : undefined,
    };

    if (noMuiStyle) {
      return (
        <NextLink className={className} ref={ref} {...nextjsProps} {...other} />
      );
    }

    return (
      <MuiLink
        component={NextLink}
        className={className}
        ref={ref}
        underline={underline}
        {...nextjsProps}
        {...other}
      />
    );
  },
);
